import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction, useCallback,
  useContext, useMemo,
  useState
} from "react";
import {ConfigInterface} from "../../config";
import moment from "moment";
import {LearnCategory, LearnModule} from "../../api/repositories/learn-repository";
import {Team, useApiCall, useApiEndpoint} from "../../api";
import {getModulesInParentCategories} from "../services/modules";
import {getSubTeamsRecursively} from "@jane/frontend-medewerker/src/pages/learn/resolveSubTeams";

export interface LearnDashboardExplorerContextType {
  // Date Range selection
  fromDate: Date
  setFromDate: Dispatch<SetStateAction<Date>>
  toDate: Date
  setToDate: Dispatch<SetStateAction<Date>>
  setLast30Days: () => void
  setLast90Days: () => void

  // Resources
  categories: LearnCategory[]
  modules: LearnModule[]
  teams: Team[]
  explorerTeams: LearnDashboardExplorerTeam[]

  // Checked States
  selectedCategories: string[],
  setSelectedCategories: Dispatch<SetStateAction<string[]>>
  selectedModuleIds: string[],
  getModulesForCategories: (categoryIds: string[]) => string[],

  // Toggle
  showModuleTable: boolean,
  setShowModuleTable: Dispatch<SetStateAction<boolean>>
}

export const LearnDashboardExplorerContext = createContext<LearnDashboardExplorerContextType>({} as LearnDashboardExplorerContextType)

export interface LearnDashboardExplorerTeam extends Team {
  hasMyTeamAsChild: boolean
  isMyTeam: boolean
}

export function LearnDashboardExplorerContextProvider(props:{children: ReactNode, config: ConfigInterface}): ReactNode {
  const [fromDate, setFromDate] = useState(moment().subtract(30, 'days').toDate())
  const [toDate, setToDate] = useState(new Date())
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [showModuleTable, setShowModuleTable] = useState<boolean>(false)

  const {learn, teams} = useApiCall(props.config)
  const { resource: categoryResource, reload: reloadCategories} = useApiEndpoint(() => learn.listCategories())
  const { resource: moduleResource, reload: reloadModules } = useApiEndpoint(() => learn.listModules())
  const { resource: teamsResource, reload: reloadTeams } = useApiEndpoint(() => teams.all())
  const { resource: myTeamsResource, reload: reloadMyTeams } = useApiEndpoint(() => teams.myTeams())

  const explorerTeams = useMemo(() => {
    const myTeamIds = myTeamsResource?.map((team) => {
      return [team, ...getSubTeamsRecursively(teamsResource ?? [], team.id)]
    }).flat().map(team => team.id) ?? []
    const myDirectTeamIds = (myTeamsResource??[]).map(team => team.id)
    return (teamsResource??[])
      .map((team): LearnDashboardExplorerTeam => {
        const teamAndSubTeamIds = [team, ...getSubTeamsRecursively(teamsResource ?? [], team.id)].map(team => team.id)
        return {
          ...team,
          hasMyTeamAsChild: teamAndSubTeamIds.some(teamId => myDirectTeamIds.includes(teamId)),
          isMyTeam: myTeamIds.includes(team.id),
        }
      })
  }, [teamsResource, myTeamsResource])

  const getModulesForCategories = useCallback((categoryIds: string[]): string[] => {
    return getModulesInParentCategories(categoryResource ?? [], moduleResource ?? [], categoryIds).map(m => m.id)
  }, [moduleResource, categoryResource])

  const selectedModuleIds = useMemo(() => {
    return getModulesForCategories(selectedCategories)
  }, [selectedCategories, getModulesForCategories])

  return <LearnDashboardExplorerContext.Provider value={{
    // Date Range selection
    fromDate: fromDate,
    setFromDate: setFromDate,
    toDate: toDate,
    setToDate: setToDate,
    setLast30Days: () => {
      setFromDate(new Date(moment().subtract(30, 'days').toDate()))
      setToDate(new Date(moment().toDate()))
    },
    setLast90Days: () => {
      setFromDate(new Date(moment().subtract(90, 'days').toDate()))
      setToDate(new Date(moment().toDate()))
    },

    // Resources
    categories: categoryResource ?? [],
    modules: moduleResource ?? [],
    teams: teamsResource ?? [],
    explorerTeams,

    // Selected Categories
    selectedCategories,
    setSelectedCategories,
    selectedModuleIds,
    getModulesForCategories,

    // Toggle
    showModuleTable,
    setShowModuleTable

  }}>{props.children}</LearnDashboardExplorerContext.Provider>
}

export function useLearnDashboardExplorerContext(): LearnDashboardExplorerContextType {
  return useContext(LearnDashboardExplorerContext)
}